/* =========================================================
   fj-predictive-search.js
   Predictive search via Shopify's Predictive Search API +
   Section Rendering API (sections/fj-predictive-search-results.liquid).

   One FjPredictiveSearch instance per [data-fj-predictive-search] root:
   - "modal"    → mobile search drawer (snippets/fj-header-search-mobile)
   - "dropdown" → desktop header search (sections/fj-header)
   ========================================================= */

class FjPredictiveSearch {
  constructor(root) {
    this.root = root;
    this.mode = root.dataset.fjPredictiveSearch;
    this.url = root.dataset.fjPredictiveSearchUrl;
    this.input = root.querySelector('[data-fj-predictive-search-input]');
    this.results = root.querySelector('[data-fj-predictive-search-results]');
    this.defaultContent = root.querySelector('[data-fj-predictive-search-default]');

    if (!this.url || !this.input || !this.results) return;

    this.debounceTimer = null;
    this.abortController = null;

    this.bindEvents();
  }

  bindEvents() {
    this.input.addEventListener('input', () => this.onInput());

    if (this.mode !== 'dropdown') return;

    this.input.addEventListener('focus', () => this.showResults());

    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.hideResults();
    });

    document.addEventListener('click', (event) => {
      if (!this.root.contains(event.target)) this.hideResults();
    });
  }

  onInput() {
    const term = this.input.value.trim();

    clearTimeout(this.debounceTimer);

    if (term.length < 2) {
      this.abortRequest();
      this.showDefault();
      return;
    }

    this.debounceTimer = setTimeout(() => this.fetchResults(term), 300);
  }

  async fetchResults(term) {
    /* Cancel the previous request so a slow, older response can't
       overwrite the results for what the shopper has typed since. */
    this.abortRequest();
    this.abortController = new AbortController();

    /* resources[type] is ONE comma-separated value. Sending the param
       twice means Shopify only keeps the last one (query), so products
       were never requested. */
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,query',
      'resources[limit]': '10',
      section_id: 'fj-predictive-search-results'
    });

    try {
      const response = await fetch(`${this.url}?${params.toString()}`, {
        signal: this.abortController.signal
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-fj-predictive-search-content]');

      if (!content) return;

      this.results.replaceChildren(content);
      this.showResults();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('fj-predictive-search: request failed', error);
    }
  }

  abortRequest() {
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
  }

  showDefault() {
    if (this.defaultContent) {
      this.results.replaceChildren(this.defaultContent);
      return;
    }

    this.results.replaceChildren();
    this.hideResults();
  }

  showResults() {
    if (this.mode !== 'dropdown' || !this.results.childElementCount) return;

    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  hideResults() {
    if (this.mode !== 'dropdown') return;

    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  reset() {
    clearTimeout(this.debounceTimer);
    this.abortRequest();
    this.input.value = '';
    this.showDefault();
  }
}

/* Mobile drawer open/close. Owns the modal only — searching is
   delegated to the FjPredictiveSearch instance inside it. */
class FjSearchModal {
  constructor(modal, search) {
    this.modal = modal;
    this.search = search;
    this.toggle = document.querySelector('[data-fj-search-toggle]');
    this.closeButtons = modal.querySelectorAll('[data-fj-search-close]');

    if (!this.toggle) return;

    this.toggle.addEventListener('click', () => this.open());
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
  }

  open() {
    this.modal.classList.add('fj-search-modal--open');
    document.body.classList.add('fj-scroll-lock');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.search.input.focus();
  }

  close() {
    this.modal.classList.remove('fj-search-modal--open');
    document.body.classList.remove('fj-scroll-lock');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.search.reset();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-fj-predictive-search]').forEach((root) => {
    const search = new FjPredictiveSearch(root);
    const modal = root.closest('[data-fj-search-modal]');

    if (modal) new FjSearchModal(modal, search);
  });
});
